"use client"

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getAuthHeader } from '../utils/auth'; // ★ 1. 認証ヘッダー取得関数をインポート

// APIサーバーのURL
const API_URL = "https://salesbreaker.jp";

// --- ▼▼▼ UIコンポーネント ▼▼▼ ---

/**
 * アコーディオンUIの基本コンポーネント
 */
const AccordionItem = ({ title, children, startOpen = false }) => {
  const [isOpen, setIsOpen] = useState(startOpen);

  return (
    <div className="border border-gray-200 rounded-lg mb-4 bg-white shadow-sm">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center p-4 text-left font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none"
      >
        <span>{title}</span>
        <svg
          className={`w-5 h-5 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </button>
      {isOpen && (
        <div className="p-4 border-t border-gray-200">
          {children}
        </div>
      )}
    </div>
  );
};

/**
 * 新規VPS追加/編集モーダルコンポーネント (プロバイダー対応版)
 */
const VpsModal = ({ isOpen, onClose, onSave, vps, providers }) => {
  const [formData, setFormData] = useState({
    vps_id: '', name: '', url: '', processes: 4, p_limit: 2, is_active: true, provider_name: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (vps) {
      setFormData({
        vps_id: vps.vps_id || '',
        name: vps.name || '',
        url: vps.url || '',
        processes: vps.processes || 4,
        p_limit: vps.p_limit ?? 2,
        is_active: vps.is_active,
        provider_name: vps.provider_name || '',
      });
    } else {
      setFormData({ vps_id: '', name: '', url: '', processes: 4, p_limit: 2, is_active: true, provider_name: '' });
    }
  }, [vps, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await onSave(formData, vps?.id);
      onClose();
    } catch (err) {
      setError(err.message || '保存中にエラーが発生しました。');
    } finally {
      setIsLoading(false);
    }
  };

  const isEditMode = !!vps;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg">
        <div className="p-6 border-b"><h3 className="text-xl font-bold">{isEditMode ? 'VPS情報の編集' : '新しいVPSの追加'}</h3></div>
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
            {error && <div className="p-3 bg-red-100 text-red-700 rounded-md">{error}</div>}
            <div>
              <label className="block text-sm font-medium text-gray-700">VPS ID (vps-01など)</label>
              <input type="text" name="vps_id" value={formData.vps_id} onChange={handleChange} disabled={isEditMode} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100" required />
              {isEditMode && <p className="text-xs text-gray-500 mt-1">VPS IDは変更できません。</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">管理名</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">プロバイダー (契約会社)</label>
              <input
                type="text"
                name="provider_name"
                value={formData.provider_name}
                onChange={handleChange}
                list="provider-list"
                className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="ConoHa, Kagoyaなどを入力または選択"
              />
              <datalist id="provider-list">
                {providers.map(p => <option key={p.id} value={p.provider_name} />)}
              </datalist>
               <p className="text-xs text-gray-500 mt-1">既存の会社は候補から選択、新しい会社は直接入力してください。</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">URL</label>
              <input type="text" name="url" value={formData.url} onChange={handleChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" placeholder="http://123.45.67.89:3173" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">PM2 並列プロセス数</label>
              <input type="number" name="processes" value={formData.processes} onChange={handleChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" min="1" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">処理能力倍率 (p_limit)</label>
              <input type="number" name="p_limit" value={formData.p_limit} onChange={handleChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" min="1" required />
              <p className="text-xs text-gray-500 mt-1">このVPSの処理能力を何倍にするか設定します。(例: 2)</p>
            </div>
            <div className="flex items-center">
              <input id="is_active" name="is_active" type="checkbox" checked={!!formData.is_active} onChange={handleChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">このVPSを有効にする</label>
            </div>
          </div>
          <div className="p-4 bg-gray-50 border-t flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded-md text-sm font-medium hover:bg-gray-300">キャンセル</button>
            <button type="submit" disabled={isLoading} className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:bg-indigo-300">{isLoading ? '保存中...' : '保存'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- ▼▼▼ 設定セクション別コンポーネント ▼▼▼ ---

/**
 * プラン・料金設定セクション
 */
const PlanSettings = ({ user }) => {
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [newPlanData, setNewPlanData] = useState({
        plan_id_string: 'starter',
        price: '',
        stripe_price_id: '',
        allocated_processes: '',
        max_parallel_jobs: '',
    });

    const fetchProducts = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/admin/products`, { // ★ 2. 認証ヘッダーを追加
                headers: getAuthHeader()
            });
            const data = await response.json();
            if (!data.success) throw new Error(data.error);
            setProducts(data.products);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const handleUpdate = (id, field, value) => {
        setProducts(current => current.map(p => p.id === id ? { ...p, [field]: value } : p));
    };

    const handleSave = async (product) => {
        try {
            const response = await fetch(`${API_URL}/api/admin/products/${product.id}`, {
                method: 'PUT',
                headers: { // ★ 2. 認証ヘッダーを追加
                    'Content-Type': 'application/json',
                    ...getAuthHeader()
                },
                body: JSON.stringify(product),
            });
            const data = await response.json();
            if (!data.success) throw new Error(data.error);
            alert(data.message);
            fetchProducts();
        } catch (err) {
            alert(`保存エラー: ${err.message}`);
        }
    };

    const handleToggleActive = async (product) => {
        const newStatus = product.is_active ? 0 : 1;
        if (newStatus === 1 && !window.confirm(`「${product.plan_id_string}」のこの価格プランを有効にしますか？\n同じプラン名の他のプランは自動的に無効になります。`)) {
            return;
        }
        try {
            const response = await fetch(`${API_URL}/api/admin/products/toggle-active/${product.id}`, {
                method: 'PUT',
                headers: { // ★ 2. 認証ヘッダーを追加
                    'Content-Type': 'application/json',
                    ...getAuthHeader()
                },
                body: JSON.stringify({ plan_id_string: product.plan_id_string, is_active: newStatus }),
            });
            const data = await response.json();
            if (!data.success) throw new Error(data.error);
            alert(data.message);
            fetchProducts();
        } catch (err) {
            alert(`状態更新エラー: ${err.message}`);
        }
    };

    const handleNewPlanChange = (e) => {
        const { name, value } = e.target;
        setNewPlanData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddNewPlan = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`${API_URL}/api/admin/products`, {
                method: 'POST',
                headers: { // ★ 2. 認証ヘッダーを追加
                    'Content-Type': 'application/json',
                    ...getAuthHeader()
                },
                body: JSON.stringify(newPlanData),
            });
            const data = await response.json();
            if (!data.success) throw new Error(data.error);
            alert(data.message);
            setIsAdding(false);
            setNewPlanData({ plan_id_string: 'starter', price: '', stripe_price_id: '', allocated_processes: '', max_parallel_jobs: '' });
            fetchProducts();
        } catch (err) {
            alert(`新規プラン追加エラー: ${err.message}`);
        }
    };

    if (isLoading) return <div className="text-center p-4">プラン情報を読み込み中...</div>;
    if (error) return <div className="bg-red-100 text-red-700 p-4 rounded-md">{`エラー: ${error}`}</div>;

    const planNames = [...new Set(products.map(p => p.plan_id_string))];

    return (
        <div className="space-y-6">
            <div className="flex justify-end">
                <button onClick={() => setIsAdding(!isAdding)} className="px-4 py-2 bg-green-600 text-white font-semibold rounded-md shadow-sm hover:bg-green-700 text-sm">
                    {isAdding ? 'キャンセル' : '+ 新しい価格プランを追加'}
                </button>
            </div>

            {isAdding && (
                <form onSubmit={handleAddNewPlan} className="p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-4">
                    <h4 className="font-semibold">新規価格プラン</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">プラン名</label>
                            <select name="plan_id_string" value={newPlanData.plan_id_string} onChange={handleNewPlanChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
                                {planNames.map(name => <option key={name} value={name}>{name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">料金 (円)</label>
                            <input type="number" name="price" value={newPlanData.price} onChange={handleNewPlanChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" required />
                        </div>
                        <div className="col-span-1 md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700">Stripe価格ID</label>
                            <input type="text" name="stripe_price_id" value={newPlanData.stripe_price_id} onChange={handleNewPlanChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">処理能力(プロセス)</label>
                            <input type="number" name="allocated_processes" value={newPlanData.allocated_processes} onChange={handleNewPlanChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">最大ジョブ数</label>
                            <input type="number" name="max_parallel_jobs" value={newPlanData.max_parallel_jobs} onChange={handleNewPlanChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" required />
                        </div>
                    </div>
                    <div className="text-right">
                        <button type="submit" className="px-4 py-2 bg-indigo-600 text-white font-semibold rounded-md shadow-sm hover:bg-indigo-700">追加して有効化</button>
                    </div>
                </form>
            )}

            <div className="divide-y divide-gray-100 md:hidden">
                {products.map(p => (
                    <div key={`${p.id}-mobile`} className={`p-4 ${p.is_active ? 'bg-green-50' : 'bg-gray-50'}`}>
                        <div className="mb-3 flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="font-semibold text-gray-900">{p.plan_id_string}</p>
                                <p className="mt-1 break-all text-xs text-gray-500">{p.stripe_price_id || '-'}</p>
                            </div>
                            <button onClick={() => handleToggleActive(p)} className={`shrink-0 px-2 py-1 text-xs font-semibold rounded-full ${p.is_active ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-800'}`}>
                                {p.is_active ? '有効' : '無効'}
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <label className="text-xs font-semibold text-gray-500">料金<input type="number" value={p.price || ''} onChange={e => handleUpdate(p.id, 'price', e.target.value)} className="mt-1 w-full p-2 border rounded" /></label>
                            <label className="text-xs font-semibold text-gray-500">処理能力<input type="number" value={p.allocated_processes || ''} onChange={e => handleUpdate(p.id, 'allocated_processes', e.target.value)} className="mt-1 w-full p-2 border rounded" /></label>
                            <label className="text-xs font-semibold text-gray-500">最大ジョブ数<input type="number" value={p.max_parallel_jobs || ''} onChange={e => handleUpdate(p.id, 'max_parallel_jobs', e.target.value)} className="mt-1 w-full p-2 border rounded" /></label>
                            <button onClick={() => handleSave(p)} className="self-end rounded-md bg-indigo-600 px-3 py-2 text-xs font-semibold text-white">保存</button>
                        </div>
                        <label className="mt-3 block text-xs font-semibold text-gray-500">Stripe価格ID<input type="text" value={p.stripe_price_id || ''} onChange={e => handleUpdate(p.id, 'stripe_price_id', e.target.value)} className="mt-1 w-full p-2 border rounded" /></label>
                    </div>
                ))}
            </div>
            <div className="hidden overflow-x-auto md:block">
                <table className="w-full text-sm text-left text-gray-600">
                    <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                        <tr>
                            <th className="py-3 px-4">プラン名</th>
                            <th className="py-3 px-4">料金</th>
                            <th className="py-3 px-4">Stripe価格ID</th>
                            <th className="py-3 px-4">処理能力(プロセス)</th>
                            <th className="py-3 px-4">最大ジョブ数</th>
                            <th className="py-3 px-4">状態</th>
                            <th className="py-3 px-4 text-right">操作</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {products.map(p => (
                            <tr key={p.id} className={p.is_active ? 'bg-green-50' : 'bg-gray-50'}>
                                <td className="py-3 px-4 font-semibold">{p.plan_id_string}</td>
                                <td className="py-3 px-4"><input type="number" value={p.price || ''} onChange={e => handleUpdate(p.id, 'price', e.target.value)} className="w-24 p-1 border rounded" /></td>
                                <td className="py-3 px-4"><input type="text" value={p.stripe_price_id || ''} onChange={e => handleUpdate(p.id, 'stripe_price_id', e.target.value)} className="w-48 p-1 border rounded" /></td>
                                <td className="py-3 px-4"><input type="number" value={p.allocated_processes || ''} onChange={e => handleUpdate(p.id, 'allocated_processes', e.target.value)} className="w-20 p-1 border rounded" /></td>
                                <td className="py-3 px-4"><input type="number" value={p.max_parallel_jobs || ''} onChange={e => handleUpdate(p.id, 'max_parallel_jobs', e.target.value)} className="w-20 p-1 border rounded" /></td>
                                <td className="py-3 px-4">
                                    <button onClick={() => handleToggleActive(p)} className={`px-2 py-1 text-xs font-semibold rounded-full ${p.is_active ? 'bg-green-200 text-green-800 hover:bg-green-300' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}>
                                        {p.is_active ? '有効' : '無効'}
                                    </button>
                                </td>
                                <td className="py-3 px-4 text-right">
                                    <button onClick={() => handleSave(p)} className="px-3 py-1 bg-indigo-600 text-white text-xs font-semibold rounded-md hover:bg-indigo-700">保存</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

/**
 * システム共通設定セクション
 */
const SystemSettings = ({ user }) => {
    const [settings, setSettings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchSettings = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/admin/system-settings`, { // ★ 2. 認証ヘッダーを追加
                headers: getAuthHeader()
            });
            const data = await response.json();
            if (!data.success) throw new Error(data.error);
            setSettings(data.settings);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleSettingChange = (key, value) => {
        setSettings(currentSettings =>
            currentSettings.map(s => s.setting_key === key ? { ...s, setting_value: value } : s)
        );
    };

    const handleSaveSetting = async (setting) => {
        try {
            const response = await fetch(`${API_URL}/api/admin/system-settings`, {
                method: 'PUT',
                headers: { // ★ 2. 認証ヘッダーを追加
                    'Content-Type': 'application/json',
                    ...getAuthHeader()
                },
                body: JSON.stringify({
                    setting_key: setting.setting_key,
                    setting_value: setting.setting_value
                }),
            });
            const data = await response.json();
            if (!data.success) throw new Error(data.error);
            alert(data.message);
        } catch (err) {
            alert(`保存エラー: ${err.message}`);
        }
    };

    if (isLoading) return <div className="text-center p-4">システム設定を読み込み中...</div>;
    if (error) return <div className="bg-red-100 text-red-700 p-4 rounded-md">{`エラー: ${error}`}</div>;

    return (
        <div className="space-y-4">
            {settings.map(setting => (
                <div key={setting.setting_key} className="flex items-center justify-between p-2 rounded-md hover:bg-gray-50">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">{setting.description}</label>
                        <p className="text-xs text-gray-500 font-mono">{setting.setting_key}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            value={setting.setting_value}
                            onChange={(e) => handleSettingChange(setting.setting_key, e.target.value)}
                            className="w-48 px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <button
                            onClick={() => handleSaveSetting(setting)}
                            className="px-3 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-md shadow-sm hover:bg-indigo-700"
                        >
                            保存
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
};

/**
 * VPSプール管理セクション
 */
const VpsPoolManagement = ({ user }) => {
  const [vpsList, setVpsList] = useState([]);
  const [providers, setProviders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVps, setEditingVps] = useState(null);

  const fetchData = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ role_for_auth: user.role, email_for_auth: user.email });
      
      const [vpsRes, providersRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/vps-pool?${params.toString()}`, { // ★ 2. 認証ヘッダーを追加
            headers: getAuthHeader()
        }),
        fetch(`${API_URL}/api/admin/providers?${params.toString()}`, { // ★ 2. 認証ヘッダーを追加
            headers: getAuthHeader()
        })
      ]);

      if (!vpsRes.ok) throw new Error('VPSリストの取得に失敗しました。');
      const vpsData = await vpsRes.json();
      setVpsList(vpsData.vpsList);

      if (providersRes.ok) {
        const providersData = await providersRes.json();
        setProviders(providersData.providers || []);
      } else {
        console.warn('プロバイダーリストの取得に失敗しました。手入力のみ可能です。');
        setProviders([]);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveVps = async (formData, vpsId) => {
    const isEditMode = !!vpsId;
    const url = isEditMode ? `${API_URL}/api/admin/vps-pool/${vpsId}` : `${API_URL}/api/admin/vps-pool`;
    const method = isEditMode ? 'PUT' : 'POST';
    const params = new URLSearchParams({ role_for_auth: user.role, email_for_auth: user.email });

    try {
      const response = await fetch(`${url}?${params.toString()}`, {
        method: method,
        headers: { // ★ 2. 認証ヘッダーを追加
            'Content-Type': 'application/json',
            ...getAuthHeader()
        },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || '保存に失敗しました。');
      alert(data.message);
      fetchData();
    } catch (err) {
      console.error('VPS保存エラー:', err);
      throw err;
    }
  };

  const handleDeleteVps = async (vps) => {
    if (window.confirm(`本当にVPS「${vps.name}」を削除しますか？`)) {
      try {
        const params = new URLSearchParams({ role_for_auth: user.role, email_for_auth: user.email });
        const response = await fetch(`${API_URL}/api/admin/vps-pool/${vps.id}?${params.toString()}`, { // ★ 2. 認証ヘッダーを追加
            method: 'DELETE',
            headers: getAuthHeader()
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || '削除に失敗しました。');
        alert(data.message);
        fetchData();
      } catch (err) {
        alert(`エラー: ${err.message}`);
      }
    }
  };

  const openAddModal = () => { setEditingVps(null); setIsModalOpen(true); };
  const openEditModal = (vps) => { setEditingVps(vps); setIsModalOpen(true); };

  if (isLoading) return <div className="text-center p-4">VPSリストを読み込み中...</div>;
  if (error) return <div className="bg-red-100 text-red-700 p-4 rounded-md">{`エラー: ${error}`}</div>;
  
  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={openAddModal}
          className="w-full px-4 py-2 bg-indigo-600 text-white font-semibold rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 text-sm sm:w-auto"
        >
          + 新規VPSを追加
        </button>
      </div>
      <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 md:hidden">
        {vpsList.map((vps) => (
          <div key={`${vps.id}-mobile`} className="bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-gray-900">{vps.name}</p>
                <p className="mt-1 break-all font-mono text-xs text-gray-500">{vps.vps_id}</p>
              </div>
              {vps.is_active ? (
                <span className="shrink-0 px-2 py-1 text-xs font-semibold text-green-800 bg-green-100 rounded-full">有効</span>
              ) : (
                <span className="shrink-0 px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-200 rounded-full">無効</span>
              )}
            </div>
            <div className="mt-3 space-y-1 text-xs text-gray-600">
              <p><span className="font-semibold text-gray-500">プロバイダー:</span> {vps.provider_name || '未設定'}</p>
              <p className="break-all"><span className="font-semibold text-gray-500">URL:</span> {vps.url}</p>
              <p><span className="font-semibold text-gray-500">並列プロセス数:</span> {vps.processes}</p>
              <p><span className="font-semibold text-gray-500">処理能力倍率:</span> {vps.p_limit}</p>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button onClick={() => openEditModal(vps)} className="rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700">編集</button>
              <button onClick={() => handleDeleteVps(vps)} className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">削除</button>
            </div>
          </div>
        ))}
        {vpsList.length === 0 && <div className="text-center p-8 text-gray-500">登録されているVPSはありません。</div>}
      </div>
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm text-left text-gray-600">
          <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
            <tr>
              <th className="py-3 px-4">VPS ID</th>
              <th className="py-3 px-4">管理名</th>
              <th className="py-3 px-4">プロバイダー</th>
              <th className="py-3 px-4">URL</th>
              <th className="py-3 px-4 text-center">並列プロセス数</th>
              <th className="py-3 px-4 text-center">処理能力倍率</th>
              <th className="py-3 px-4 text-center">ステータス</th>
              <th className="py-3 px-4 text-right">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {vpsList.map((vps) => (
              <tr key={vps.id} className="hover:bg-gray-50">
                <td className="py-3 px-4 font-mono text-xs">{vps.vps_id}</td>
                <td className="py-3 px-4 font-medium">{vps.name}</td>
                <td className="py-3 px-4">{vps.provider_name || <span className="text-gray-400">未設定</span>}</td>
                <td className="py-3 px-4 font-mono text-xs">{vps.url}</td>
                <td className="py-3 px-4 text-center font-bold">{vps.processes}</td>
                <td className="py-3 px-4 text-center font-bold">{vps.p_limit}</td>
                <td className="py-3 px-4 text-center">
                  {vps.is_active ? (
                    <span className="px-2 py-1 text-xs font-semibold text-green-800 bg-green-100 rounded-full">有効</span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-200 rounded-full">無効</span>
                  )}
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => openEditModal(vps)} className="px-3 py-1 bg-white border border-gray-300 text-gray-700 text-xs font-semibold rounded-md hover:bg-gray-50">編集</button>
                    <button onClick={() => handleDeleteVps(vps)} className="px-3 py-1 bg-red-50 border border-red-200 text-red-700 text-xs font-semibold rounded-md hover:bg-red-100">削除</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {vpsList.length === 0 && <div className="text-center p-8 text-gray-500">登録されているVPSはありません。</div>}
      </div>
      <VpsModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSaveVps} vps={editingVps} providers={providers} />
    </div>
  );
};


/**
 * メインページコンポーネント
 */
export default function VpsManagementPage() {
  const { user: contextUser } = useOutletContext() || {};
  const user = useMemo(() => contextUser || { role: 'admin', email: 'admin@example.com' }, [contextUser]);

  return (
    <div className="p-3 sm:p-4 md:p-8 bg-gray-50 min-h-full">
      <div className="max-w-screen-xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">⚙️ システム設定</h1>
          <p className="text-gray-500 mt-1">システムの各種設定をここから管理できます。</p>
        </div>
        
        <AccordionItem title="📈 プラン・料金設定" startOpen={true}>
          <PlanSettings user={user} />
        </AccordionItem>
        
        <AccordionItem title="🔧 システム共通設定">
          <SystemSettings user={user} />
        </AccordionItem>

        <AccordionItem title="🤖 VPSプール管理">
          <VpsPoolManagement user={user} />
        </AccordionItem>
      </div>
    </div>
  );
}
